import { useState, useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { addNotification } from '../store';
import { useGetLatestPresentationBuildQuery } from '../services/presentations.service';
import { config } from '../config';

interface UsePresentationBuildPollingOptions {
  showNotifications?: boolean;
  hasSlides?: boolean;
}

export const usePresentationBuildPolling = (
  presentationId: number | string | undefined,
  options: UsePresentationBuildPollingOptions = {},
) => {
  const { showNotifications = true, hasSlides = false } = options;
  const dispatch = useDispatch();

  const [isPresentationBuilding, setIsPresentationBuilding] = useState(false);
  const [pollStartTime, setPollStartTime] = useState<number | null>(null);
  const [pollingErrorCount, setPollingErrorCount] = useState(0);

  // track the build ID we're waiting for to avoid false "complete" from old cached builds
  const waitingForBuildIdRef = useRef<number | null>(null);

  const MAX_POLLING_ERRORS = 3;

  const { data: initialBuildData } = useGetLatestPresentationBuildQuery(
    { presentationId: Number(presentationId) },
    {
      skip: !presentationId || isPresentationBuilding || !hasSlides,
    },
  );

  const { data: pollingBuildData, isError: isPollingError } = useGetLatestPresentationBuildQuery(
    { presentationId: Number(presentationId) },
    {
      pollingInterval: isPresentationBuilding ? config.defaultFetchInterval : 0,
      skip: !isPresentationBuilding || !presentationId,
    },
  );

  const buildData = isPresentationBuilding ? pollingBuildData : initialBuildData;

  const startBuildPolling = useCallback(() => {
    setIsPresentationBuilding(true);
    setPollStartTime(Date.now());
    setPollingErrorCount(0);
    waitingForBuildIdRef.current = null;
  }, []);

  const stopBuildPolling = useCallback(() => {
    setIsPresentationBuilding(false);
    setPollStartTime(null);
    setPollingErrorCount(0);
  }, []);

  // check initial build status on mount/refresh and start polling if needed
  useEffect(() => {
    if (!initialBuildData || isPresentationBuilding) return;

    const status = initialBuildData.status;
    if (status === 'pending' || status === 'running') {
      startBuildPolling();
    }
  }, [initialBuildData?.status, isPresentationBuilding, startBuildPolling]);

  // handle build status changes
  useEffect(() => {
    if (!buildData || !isPresentationBuilding) return;

    if (buildData.status === 'pending' || buildData.status === 'running') {
      if (waitingForBuildIdRef.current !== buildData.id) {
        waitingForBuildIdRef.current = buildData.id;
      }
      return;
    }

    const isExpectedBuild = waitingForBuildIdRef.current === buildData.id;
    if (!isExpectedBuild && waitingForBuildIdRef.current === null) {
      return;
    }

    if (buildData.status === 'completed') {
      setIsPresentationBuilding(false);
      setPollStartTime(null);

      if (showNotifications && isExpectedBuild) {
        dispatch(
          addNotification({
            type: 'success',
            message: 'Presentation build complete',
          }),
        );
      }
    } else if (buildData.status === 'cancelled') {
      setIsPresentationBuilding(false);
      setPollStartTime(null);
    } else if (buildData.status === 'errored') {
      setIsPresentationBuilding(false);
      setPollStartTime(null);

      if (showNotifications && isExpectedBuild) {
        const errorMessage = buildData.details?.[0] || 'Failed to build presentation';
        dispatch(
          addNotification({
            type: 'error',
            message: errorMessage,
          }),
        );
      }
    }
  }, [buildData?.status, buildData?.id, buildData?.details, dispatch, showNotifications, isPresentationBuilding]);

  // handle polling error (API error) - tolerate some errors before giving up
  useEffect(() => {
    if (isPollingError && isPresentationBuilding) {
      const newErrorCount = pollingErrorCount + 1;
      setPollingErrorCount(newErrorCount);

      if (newErrorCount >= MAX_POLLING_ERRORS) {
        setIsPresentationBuilding(false);
        setPollStartTime(null);
        setPollingErrorCount(0);

        if (showNotifications) {
          dispatch(
            addNotification({
              type: 'error',
              message: 'Failed to check presentation build status',
            }),
          );
        }
      }
    }
  }, [isPollingError, isPresentationBuilding, dispatch, showNotifications, pollingErrorCount]);

  // reset error count on successful poll
  useEffect(() => {
    if (pollingBuildData && isPresentationBuilding && pollingErrorCount > 0) {
      setPollingErrorCount(0);
    }
  }, [pollingBuildData, isPresentationBuilding, pollingErrorCount]);

  // handle polling timeout
  useEffect(() => {
    if (!isPresentationBuilding || !pollStartTime) return;

    const checkTimeout = () => {
      const elapsed = Date.now() - pollStartTime;
      if (elapsed >= config.presentationBuildPollingTimeout) {
        setIsPresentationBuilding(false);
        setPollStartTime(null);

        if (showNotifications) {
          dispatch(
            addNotification({
              type: 'error',
              message: 'Presentation build timeout',
            }),
          );
        }
      }
    };

    const timeoutId = setInterval(checkTimeout, 1000);
    return () => clearInterval(timeoutId);
  }, [isPresentationBuilding, pollStartTime, dispatch, showNotifications]);

  return {
    buildData,
    isPresentationBuilding,
    isPollingError,
    startBuildPolling,
    stopBuildPolling,
  };
};
